import { useState, useEffect } from 'react'
import { Clock, UserX, User, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import api from '../services/api'

const UserDeletionLogs = () => {
    const [logs, setLogs] = useState([])
    const [loading, setLoading] = useState(true)
    const [expanded, setExpanded] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)

    useEffect(() => {
        if (expanded) {
            fetchLogs()
        }
    }, [expanded, currentPage])

    const fetchLogs = async () => {
        try {
            setLoading(true)
            const response = await api.get(`/users/deletion-logs?page=${currentPage}&limit=10`)
            setLogs(response.data.data.logs)
            setTotalPages(response.data.data.pagination.pages)
        } catch (error) {
            console.error('Error fetching deletion logs:', error)
        } finally {
            setLoading(false)
        }
    }

    const formatDate = (date) => {
        return new Date(date).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    return (
        <div className="bg-white rounded-xl shadow p-4 mt-4">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition"
            >
                <div className="flex items-center space-x-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                        <Trash2 className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="text-left">
                        <h3 className="text-lg font-semibold text-gray-900">User Deletion Logs</h3>
                        <p className="text-sm text-gray-500">View history of user deletions</p>
                    </div>
                </div>
                {expanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
            </button>

            {expanded && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="text-center py-8">
                            <UserX className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Deletion Logs</h3>
                            <p className="text-gray-600">No user deletions have been recorded yet.</p>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-3">
                                {logs.map((log) => (
                                    <div
                                        key={log._id}
                                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <div className="p-2 bg-red-100 rounded-lg">
                                                        <UserX className="h-4 w-4 text-red-600" />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center space-x-2">
                                                            <span className="font-semibold text-gray-900">
                                                                {log.deletedUserInfo?.username || 'Unknown User'}
                                                            </span>
                                                            <span className="text-gray-400">•</span>
                                                            <span className="text-sm text-gray-600">
                                                                {log.deletedUserInfo?.email || 'N/A'}
                                                            </span>
                                                        </div>
                                                        {log.deletedUserInfo?.role?.name && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                                                                {log.deletedUserInfo.role.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="ml-11 space-y-1">
                                                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                                                        <User className="h-3 w-3" />
                                                        <span>
                                                            Deleted by: <span className="font-medium text-gray-900">
                                                                {log.deletedByInfo?.username || 'Unknown'}
                                                            </span>
                                                            {log.deletedByInfo?.email && (
                                                                <span className="text-gray-500 ml-1">
                                                                    ({log.deletedByInfo.email})
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                                                        <Clock className="h-3 w-3" />
                                                        <span>
                                                            {formatDate(log.deletedAt)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                                    <div className="text-sm text-gray-700">
                                        Page {currentPage} of {totalPages}
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                            disabled={currentPage === 1}
                                            className="btn btn-outline text-sm py-1 px-3"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                            disabled={currentPage === totalPages}
                                            className="btn btn-outline text-sm py-1 px-3"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    )
}

export default UserDeletionLogs

